import { escapeHtml } from "./text.ts";

/** A compact, progressively enhanced multi-select. The original select owns form data. */
export function bindSearchableSelections(form: HTMLFormElement, selector = 'select[multiple][name="assignedTo"], select[multiple][name="dependencyIds"], select[multiple][name="relatedTaskIds"]'): void {
  form.querySelectorAll<HTMLSelectElement>(selector).forEach(select => {
    if (select.dataset.searchableBound) return;
    const label = select.closest("label");
    if (!label) return;
    const title = label.firstChild?.textContent?.trim() || select.name;
    const noun = select.name === "dependencyIds" ? "blockers" : title.toLowerCase();
    const wrapper = form.ownerDocument.createElement("div");
    wrapper.className = "field-label selection-field";
    wrapper.dataset.selectionField = select.name;
    const heading = form.ownerDocument.createElement("span");
    heading.textContent = title;
    wrapper.append(heading);
    label.replaceWith(wrapper);
    wrapper.append(select);
    label.querySelectorAll("small").forEach(note => wrapper.append(note));
    select.hidden = true;
    select.setAttribute("aria-label", title);
    select.dataset.searchableBound = "true";
    const selected = form.ownerDocument.createElement("div");
    selected.className = "selection-field__chosen";
    selected.setAttribute("role", "group");
    selected.setAttribute("aria-label", `Selected ${noun}`);
    const details = form.ownerDocument.createElement("details");
    details.className = "selection-picker";
    details.innerHTML = `<summary>Choose ${escapeHtml(noun)}</summary><div class="selection-picker__body"><input type="search" aria-label="Search ${escapeHtml(noun)}" placeholder="Search…" autocomplete="off"><div class="selection-picker__options" role="group" aria-label="${escapeHtml(title)} choices"></div><p class="field-note" data-selection-empty hidden></p><button type="button" class="text-button" data-selection-done>Done</button></div>`;
    select.after(selected, details);
    const search = details.querySelector<HTMLInputElement>("input")!;
    const choices = details.querySelector<HTMLElement>(".selection-picker__options")!;
    const renderChoices = () => {
      const query = search.value.trim().toLocaleLowerCase();
      const options = Array.from(select.options).filter(option => (option.textContent || "").toLocaleLowerCase().includes(query));
      choices.replaceChildren(...options.map(option => {
        const row = form.ownerDocument.createElement("label");
        const check = form.ownerDocument.createElement("input");
        check.type = "checkbox";
        check.checked = option.selected;
        check.disabled = select.disabled || option.disabled;
        const text = form.ownerDocument.createElement("span");
        text.textContent = option.textContent;
        row.append(check, text);
        check.addEventListener("input", event => event.stopPropagation());
        check.addEventListener("change", event => {
          event.stopPropagation();
          option.selected = check.checked;
          select.dispatchEvent(new (form.ownerDocument.defaultView!.Event)("input", { bubbles: true }));
          select.dispatchEvent(new (form.ownerDocument.defaultView!.Event)("change", { bubbles: true }));
        });
        return row;
      }));
      details.querySelector<HTMLElement>("[data-selection-empty]")!.hidden = options.length > 0;
      details.querySelector<HTMLElement>("[data-selection-empty]")!.textContent = query ? "No matches." : "No options available.";
    };
    const refresh = () => {
      const selectedOptions = Array.from(select.options).filter(option => option.selected);
      const activeIndex = Array.from(selected.children).indexOf(form.ownerDocument.activeElement as Element);
      selected.replaceChildren(...selectedOptions.map(option => {
        const button = form.ownerDocument.createElement("button");
        button.type = "button";
        button.className = "selection-field__chip";
        button.textContent = `${option.textContent} ×`;
        button.setAttribute("aria-label", `Remove ${option.textContent} from ${noun}`);
        button.disabled = select.disabled;
        button.addEventListener("click", () => {
          option.selected = false;
          select.dispatchEvent(new (form.ownerDocument.defaultView!.Event)("input", { bubbles: true }));
          select.dispatchEvent(new (form.ownerDocument.defaultView!.Event)("change", { bubbles: true }));
        });
        return button;
      }));
      selected.hidden = !selectedOptions.length;
      details.querySelector("summary")!.textContent = `${selectedOptions.length ? "Change" : "Choose"} ${noun}`;
      if (activeIndex >= 0) ((selected.children[Math.min(activeIndex, selected.children.length - 1)] as HTMLElement | undefined) || details.querySelector<HTMLElement>("summary"))?.focus({ preventScroll: true });
      const visibleOptions = Array.from(select.options).filter(option => (option.textContent || "").toLocaleLowerCase().includes(search.value.trim().toLocaleLowerCase()));
      choices.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((check, index) => {
        const option = visibleOptions[index];
        if (option) check.checked = option.selected;
      });
    };
    select.addEventListener("change", refresh);
    wrapper.addEventListener("selection-refresh", refresh);
    search.addEventListener("input", event => { event.stopPropagation(); renderChoices(); });
    search.addEventListener("change", event => event.stopPropagation());
    details.addEventListener("toggle", () => { if (details.open) { search.value = ""; renderChoices(); } });
    details.querySelector("[data-selection-done]")!.addEventListener("click", () => { details.open = false; details.querySelector<HTMLElement>("summary")!.focus(); });
    details.addEventListener("keydown", event => {
      if (event.key === "Escape" && details.open) { event.preventDefault(); event.stopPropagation(); details.open = false; details.querySelector<HTMLElement>("summary")!.focus(); }
      if (event.key === "Enter" && event.target === search) event.preventDefault();
    });
    refresh();
  });
}

export function refreshSearchableSelections(form: HTMLFormElement): void {
  form.querySelectorAll("[data-selection-field]").forEach(field => field.dispatchEvent(new (form.ownerDocument.defaultView!.Event)("selection-refresh")));
}

export function focusSearchableSelection(form: HTMLFormElement, name: string): void {
  const field = Array.from(form.querySelectorAll<HTMLElement>("[data-selection-field]")).find(item => item.dataset.selectionField === name);
  const picker = field?.querySelector<HTMLDetailsElement>("details");
  if (picker) { picker.open = true; picker.querySelector<HTMLElement>("summary")?.focus(); }
  else (form.elements.namedItem(name) as HTMLElement | null)?.focus();
}
